import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { EventBadgeStamp } from "../components/EventBadgeStamp";
import { KarinCalendarStamp, KarinCalendarStampLegend } from "../components/KarinCalendarStamp";
import { computeDayPopupStyle, measureCalDayCell } from "../lib/day-popup-geometry";
import { dedupeJpRoomRowsForAdminList } from "../lib/jp-room-admin-dedupe";
import { buildMonthCalendarCells, shiftYearMonth } from "../lib/month-calendar-grid";
import {
  formatDoorCurtainSlots,
  formatTimeRange,
  getPublicEventBadge,
  isOnlineKarinStampEvent,
  jstTodayYmd,
  publicPerformersList,
  weekdayJp,
} from "../types/public";
import type { PublicEvent } from "../types/public";
import { revealAdminEventsList, scrollToAdminListRow } from "./admin-scroll-focus";
import { adminEventsListModeFromPathname, adminRowMatchesEventsListMode } from "./admin-events-mode";
import {
  adminListFilterMatchesRowWithVis,
  adminRowPassesDefaultEventDateFloor,
  buildAdminMonthChoices,
  jstCurrentYearMonth,
  parseAdminFocusDay,
  type AdminOutletFilter,
} from "./admin-list-filter";
import { useAdminEventsData } from "./admin-events-data-context";
import { buildAdminNewEventHref, inferAdminModeForRow } from "./admin-event-prefill";

type Row = Record<string, unknown>;

function parseList(v: unknown): string[] {
  if (typeof v !== "string") return [];
  try {
    const j = JSON.parse(v);
    return Array.isArray(j) ? j.map(String) : [];
  } catch {
    return [];
  }
}

function rowEventDateIsoDate(r: Row): string {
  const raw = String(r.event_date ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : "";
}

function adminRowToPublicEventStub(r: Row): PublicEvent {
  const ed = rowEventDateIsoDate(r);
  const performers = parseList(r.performers_json);
  const nz = (v: unknown) => {
    if (v == null) return null;
    const s = String(v).trim();
    return s ? s : null;
  };
  return {
    id: String(r.id ?? ""),
    source: String(r.source ?? "manual"),
    event_kind: String(r.event_kind ?? "event"),
    event_name: String(r.event_name ?? ""),
    venue: nz(r.venue),
    prefecture: nz(r.prefecture),
    event_date: ed || "2099-01-01",
    time_start: nz(r.time_start),
    time_end: nz(r.time_end),
    door_time: nz(r.door_time),
    curtain_time: nz(r.curtain_time),
    door_time_2: null,
    curtain_time_2: null,
    door_time_3: null,
    curtain_time_3: null,
    performers,
    description: nz(r.description),
    detail_url: nz(r.detail_url),
    reserve_url: nz(r.reserve_url),
    memo: nz(r.memo),
    is_karin: Boolean(Number(r.is_karin)),
    karin_subtype: nz(r.karin_subtype),
    karin_icon: nz(r.karin_icon),
    group_code: nz(r.group_code),
    reception_start: nz(r.reception_start),
    reception_end: nz(r.reception_end),
    reception_period_text: nz(r.reception_period_text),
    performance_text: nz(r.performance_text),
  };
}

function venueDisplayLineFromRow(r: Row): string {
  const pref = String(r.prefecture ?? "").trim();
  const v = String(r.venue ?? "").trim();
  if (pref && v) return `${pref} · ${v}`;
  if (v) return v;
  if (pref) return pref;
  return "会場未定";
}

function slotTimeSummaryFromRow(r: Row): string {
  const stub = adminRowToPublicEventStub(r);
  const tr = formatTimeRange(stub);
  const slots = formatDoorCurtainSlots(stub);
  const bits: string[] = [];
  if (tr && tr !== "—") bits.push(`開始〜終了 ${tr}`);
  if (slots) bits.push(slots);
  return bits.join(" ｜ ") || "時間は未定または未入力です";
}

function adminKarinStampGroupsForDay(karin: Row[]): { titleKey: string; isRemote: boolean; icon: string | null }[] {
  const m = new Map<string, { remote: boolean; icon: string | null }>();
  for (const r of karin) {
    const titleKey = String(r.event_name ?? "").trim() || String(r.id ?? "");
    const remote = isOnlineKarinStampEvent({
      event_kind: String(r.event_kind ?? "event"),
      karin_subtype: r.karin_subtype != null ? String(r.karin_subtype) : null,
    });
    const icon = String(r.karin_icon ?? "").trim() || null;
    const prev = m.get(titleKey);
    if (!prev) m.set(titleKey, { remote, icon });
    else m.set(titleKey, { remote: prev.remote || remote, icon: prev.icon ?? icon });
  }
  return [...m.entries()].map(([titleKey, v]) => ({ titleKey, isRemote: v.remote, icon: v.icon }));
}

export function AdminSharedMonthCalendar({ outletFilter }: { outletFilter: AdminOutletFilter }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { rows, fetchError } = useAdminEventsData();
  const [dayPopup, setDayPopup] = useState<ReturnType<typeof measureCalDayCell>>(null);

  const listMode = useMemo(() => adminEventsListModeFromPathname(location.pathname), [location.pathname]);

  const { adminEventDateFrom, adminEventDateTo, adminKeyword, adminVis, adminKarinSubtype } = outletFilter;

  const calendarRows = useMemo(() => {
    let mf = rows.filter((r) => adminRowMatchesEventsListMode(r, listMode));
    mf = mf.filter(
      (r) =>
        adminRowPassesDefaultEventDateFloor(listMode === "archive" ? "archive" : listMode, adminEventDateFrom, adminEventDateTo, r) &&
        adminListFilterMatchesRowWithVis(adminEventDateFrom, adminEventDateTo, adminKeyword, adminVis, r, adminKarinSubtype),
    );
    return dedupeJpRoomRowsForAdminList(mf, listMode);
  }, [rows, listMode, adminEventDateFrom, adminEventDateTo, adminKeyword, adminVis, adminKarinSubtype]);

  const adminMonthChoices = useMemo(() => buildAdminMonthChoices(), []);

  const adminCalMonth = useMemo(() => {
    const m = searchParams.get("month")?.trim();
    if (m && /^\d{4}-\d{2}$/.test(m)) return m;
    const from = adminEventDateFrom.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(from)) return from.slice(0, 7);
    return jstCurrentYearMonth();
  }, [searchParams, adminEventDateFrom]);

  const adminCalCells = useMemo(() => buildMonthCalendarCells(adminCalMonth), [adminCalMonth]);

  const adminCalDayBuckets = useMemo(() => {
    const map: Record<string, { karin: Row[]; others: Row[] }> = {};
    for (const r of calendarRows) {
      const d = rowEventDateIsoDate(r);
      if (!d || d.slice(0, 7) !== adminCalMonth) continue;
      if (!map[d]) map[d] = { karin: [], others: [] };
      if (Number(r.is_karin)) map[d]!.karin.push(r);
      else map[d]!.others.push(r);
    }
    return map;
  }, [calendarRows, adminCalMonth]);

  const adminCalFocusDay = parseAdminFocusDay(searchParams.get("focus_day"));

  const goAdminCalMonthYm = (ym: string) => {
    const next = new URLSearchParams(searchParams);
    if (ym && /^\d{4}-\d{2}$/.test(ym)) {
      next.set("month", ym);
      next.delete("ev_from");
      next.delete("ev_to");
      next.delete("focus_day");
    }
    setSearchParams(next, { replace: true });
  };

  const focusAdminCalDayInList = (dayKey: string, opts?: { toggleOffIfSame?: boolean }) => {
    const toggleOffIfSame = opts?.toggleOffIfSame ?? true;
    const onEventsList = location.pathname.startsWith("/admin/events");

    const next = new URLSearchParams(searchParams);
    next.set("focus_day", dayKey);
    next.set("month", dayKey.slice(0, 7));
    next.delete("ev_from");
    next.delete("ev_to");

    if (!onEventsList) {
      navigate(`/admin/events/all?${next.toString()}`);
      return;
    }

    if (toggleOffIfSame && adminCalFocusDay === dayKey) {
      next.delete("focus_day");
      setSearchParams(next, { replace: true });
      return;
    }

    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!adminCalFocusDay) return;
    if (!location.pathname.startsWith("/admin/events")) return;
    requestAnimationFrame(() => {
      revealAdminEventsList();
      requestAnimationFrame(() => scrollToAdminListRow(adminCalFocusDay));
    });
  }, [adminCalFocusDay, location.pathname, calendarRows.length]);

  useEffect(() => {
    setDayPopup(null);
  }, [adminCalMonth]);

  useEffect(() => {
    if (!dayPopup) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setDayPopup(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [dayPopup]);

  useEffect(() => {
    if (!dayPopup) return;
    const sync = () => {
      const next = measureCalDayCell(dayPopup.day, "#admin-shared-month-calendar");
      if (next) setDayPopup(next);
    };
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [dayPopup?.day]);

  const openEdit = (r: Row) => {
    const id = String(r.id ?? "").trim();
    if (!id) return;
    const mode = inferAdminModeForRow(r);
    setDayPopup(null);
    navigate(`/admin/events/${mode}?edit=${encodeURIComponent(id)}`);
  };

  const newEventMode = (mode: string | undefined): string => {
    if (mode === "helloproject" || mode === "jp_room" || mode === "mline") return mode;
    return "karin";
  };

  const openCreateForDay = (day: string) => {
    const mode = newEventMode(listMode);
    const source = mode === "helloproject" ? "helloproject" : mode === "jp_room" ? "jp_room" : mode === "mline" ? "mline" : "manual";
    setDayPopup(null);
    navigate(
      buildAdminNewEventHref(mode, {
        event_date: day,
        source,
        is_karin: mode === "karin" ? "1" : "0",
      }),
    );
  };

  const toggleAdminDayPopup = (dayKey: string, cellEl: HTMLElement) => {
    const r = cellEl.getBoundingClientRect();
    setDayPopup((prev) => (prev?.day === dayKey ? null : { day: dayKey, top: r.top, left: r.left, width: r.width, height: r.height }));
  };

  const portalBucket = useMemo(() => {
    if (!dayPopup) return null;
    return adminCalDayBuckets[dayPopup.day] ?? { karin: [] as Row[], others: [] as Row[] };
  }, [dayPopup, adminCalDayBuckets]);

  const modeHint = useMemo(() => {
    if (listMode === undefined || listMode === "all") return "（アーカイブ以外の予定をすべて表示。上の絞り込み条件を反映）";
    if (listMode === "karin") return "（宮本佳林予定タブと同じ行だけ）";
    if (listMode === "helloproject") return "（ハロプロ予定タブと同じ行だけ）";
    if (listMode === "jp_room") return "（J.P ROOM 予定タブと同じ表示ロジック・重複は1行に畳む）";
    if (listMode === "mline") return "（M-line 予定タブと同じ行だけ）";
    if (listMode === "pending") return "（確認待ち一覧と同じ行だけ）";
    if (listMode === "archive") return "（アーカイブ一覧と同じ行だけ）";
    return "（予定管理の表示モードに追従）";
  }, [listMode]);

  return (
    <section className="card admin-events-cal-wrap">
      {fetchError ? (
        <p className="mono" style={{ color: "#b00020", fontSize: 12, margin: "0 0 8px" }}>
          カレンダー用データの取得に失敗: {fetchError}
        </p>
      ) : null}
      <div className="home-month-row admin-cal-month-row">
        <button
          type="button"
          className="btn small secondary home-month-nav"
          aria-label="前の月"
          onClick={() => goAdminCalMonthYm(shiftYearMonth(adminCalMonth, -1))}
        >
          ‹
        </button>
        <div className="home-month-label">
          <label className="home-month-select-wrap">
            <span className="home-month-select-label">カレンダーの月（表サイトと同じ見た目）</span>
            <select className="input home-month-select" value={adminCalMonth} onChange={(e) => goAdminCalMonthYm(e.target.value)}>
              {adminMonthChoices.map((m) => (
                <option key={m} value={m}>
                  {m.replace("-", "年")}月
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          type="button"
          className="btn small secondary home-month-nav"
          aria-label="次の月"
          onClick={() => goAdminCalMonthYm(shiftYearMonth(adminCalMonth, 1))}
        >
          ›
        </button>
      </div>
      <div className="admin-cal-block cal">
        <div className="home-cal-anchor" id="admin-shared-month-calendar">
          <div className="home-cal-title-row">
            <h2 className="home-cal-title">カレンダー</h2>
            <span className="mono home-cal-month-tag">{adminCalMonth}</span>
          </div>
          <p className="home-cal-hint">
            <strong>その日のマス（枠のどこでも）</strong>をタップすると当日の予定プレビューが開きます（もう一度同じ日で閉じます）。プレビュー内の「予定管理の一覧でこの日へ移動」は<strong>全ての予定一覧</strong>へ移り、その日の行へスクロールします（他の日の予定は消しません）。
            {modeHint}
            {adminCalFocusDay ? (
              <>
                {" "}
                <button type="button" className="home-inline-link" onClick={() => focusAdminCalDayInList(adminCalFocusDay)}>
                  表示日を解除
                </button>
              </>
            ) : null}
          </p>
          <p className="home-cal-legend">
            印の見方：
            <KarinCalendarStampLegend isRemote={false} />
            ＝直接会える ／
            <KarinCalendarStampLegend isRemote />
            ＝オンライン ／色付き丸＝他グループ・M-line 所属
          </p>
          <div className="cal-head">
            {["日", "月", "火", "水", "木", "金", "土"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          <div className="cal-grid home-cal-grid admin-cal-grid">
            {adminCalCells.map((cell, idx) => {
              if (!cell.inMonth || !cell.date) {
                return <div key={idx} className="day-cell muted" />;
              }
              const dayKey = cell.date;
              const bucket = adminCalDayBuckets[dayKey] ?? { karin: [], others: [] };
              const karinStamps = adminKarinStampGroupsForDay(bucket.karin);
              const others = bucket.others;
              const isSel = adminCalFocusDay === dayKey;
              const isToday = dayKey === jstTodayYmd();
              return (
                <div
                  key={dayKey}
                  data-cal-day={dayKey}
                  role="button"
                  tabIndex={0}
                  aria-label={`${dayKey}の予定をポップアップで見る`}
                  className={`day-cell day-cell--cal-hit${isSel ? " day-cell--selected" : ""}${isToday ? " day-cell--today" : ""}`}
                  onClick={(ev) => toggleAdminDayPopup(dayKey, ev.currentTarget as HTMLElement)}
                  onKeyDown={(ev) => {
                    if (ev.key !== "Enter" && ev.key !== " ") return;
                    ev.preventDefault();
                    toggleAdminDayPopup(dayKey, ev.currentTarget as HTMLElement);
                  }}
                >
                  <div className="day-cell-day-num-row">
                    <span className="day-num">{Number(dayKey.slice(-2))}</span>
                  </div>
                  <div className="stamps">
                    {karinStamps.map(({ titleKey, isRemote, icon }) => (
                      <span key={`${dayKey}-karin-${titleKey}`} className="stamp-cal-hit stamp-karin-wrap" aria-hidden>
                        <KarinCalendarStamp
                          icon={icon}
                          isRemote={isRemote}
                          title={icon ? `宮本佳林（${icon}）` : isRemote ? "宮本佳林（オンライン）" : "宮本佳林（直接会える）"}
                        />
                      </span>
                    ))}
                    {others.slice(0, 4).map((o, oi) => (
                      <span key={String(o.id || `${dayKey}-cal-other-${oi}`)} className="cal-other-stamp-btn" title={String(o.event_name ?? "")} aria-hidden>
                        <EventBadgeStamp badge={getPublicEventBadge(adminRowToPublicEventStub(o))} size="sm" title={String(o.event_name ?? "")} />
                      </span>
                    ))}
                    {others.length > 4 ? (
                      <span className="cal-other-more-btn" aria-hidden>
                        <span style={{ fontSize: 10, color: "var(--muted)" }}>+</span>
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {dayPopup && portalBucket
        ? createPortal(
            <>
              <div className="home-day-pop-backdrop" aria-hidden onClick={() => setDayPopup(null)} />
              <div
                className="home-day-pop home-day-pop--floating"
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-day-popup-title"
                style={computeDayPopupStyle(dayPopup)}
                onClick={(ev) => ev.stopPropagation()}
              >
              <div className="home-day-pop-head">
                <h2 id="admin-day-popup-title" className="home-day-pop-title">
                  {dayPopup.day}（{weekdayJp(dayPopup.day)}）
                </h2>
                <button type="button" className="btn small secondary" onClick={() => setDayPopup(null)}>
                  閉じる
                </button>
              </div>
              {portalBucket.karin.length > 0 ? (
                <p className="home-day-pop-lead">
                  この日の佳林は<strong>{portalBucket.karin.length}公演</strong>です。
                </p>
              ) : null}
              <div className="home-day-pop-body">
                <section className="home-day-modal-section">
                  <h3 className="home-day-modal-sub">宮本佳林</h3>
                  {portalBucket.karin.length === 0 ? (
                    <p className="home-day-modal-empty">この日の予定はありません</p>
                  ) : (
                    <ul className="home-day-modal-list">
                      {portalBucket.karin.map((r, ki) => {
                        const stub = adminRowToPublicEventStub(r);
                        const perf = publicPerformersList(stub);
                        return (
                          <li key={String(r.id ?? `karin-${dayPopup.day}-${ki}`)} className="home-day-modal-li home-day-modal-li--row">
                            <div className="home-day-pop-other-text" style={{ flex: 1, minWidth: 0 }}>
                              <span className="home-day-modal-li-title">{String(r.event_name ?? "")}</span>
                              <span className="home-day-pop-venue">{venueDisplayLineFromRow(r)}</span>
                              <span className="home-day-pop-times">{slotTimeSummaryFromRow(r)}</span>
                              {perf.length ? (
                                <span className="home-day-pop-times" style={{ marginTop: 2 }}>
                                  {perf.join("、")}
                                </span>
                              ) : null}
                            </div>
                            <button type="button" className="btn small secondary" style={{ flexShrink: 0 }} onClick={() => openEdit(r)}>
                              編集
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
                <section className="home-day-modal-section">
                  <h3 className="home-day-modal-sub">その他の現場</h3>
                  {portalBucket.others.length === 0 ? (
                    <p className="home-day-modal-empty">なし</p>
                  ) : (
                    <ul className="home-day-modal-list">
                      {portalBucket.others.map((o, oi) => (
                        <li key={String(o.id || `${dayPopup.day}-pop-other-${oi}`)} className="home-day-modal-li home-day-modal-li--row">
                          <EventBadgeStamp
                            badge={getPublicEventBadge(adminRowToPublicEventStub(o))}
                            size="sm"
                            title={String(o.event_name ?? "")}
                          />
                          <div className="home-day-pop-other-text">
                            <span className="home-day-modal-li-title">{String(o.event_name ?? "")}</span>
                            <span className="home-day-pop-venue">{venueDisplayLineFromRow(o)}</span>
                            <span className="home-day-pop-times">{slotTimeSummaryFromRow(o)}</span>
                          </div>
                          <button type="button" className="btn small secondary" style={{ flexShrink: 0 }} onClick={() => openEdit(o)}>
                            編集
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
              <div className="home-day-pop-actions home-day-pop-actions--stack">
                <button
                  type="button"
                  className="btn"
                  onClick={() => openCreateForDay(dayPopup.day)}
                >
                  この日に新規予定を追加
                </button>
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => {
                    const day = dayPopup.day;
                    setDayPopup(null);
                    navigate(`/admin/announcements?notice_date=${encodeURIComponent(day)}#notice-promoting-section`);
                  }}
                >
                  告知に移動
                </button>
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => {
                    setDayPopup(null);
                    focusAdminCalDayInList(dayPopup.day, { toggleOffIfSame: false });
                  }}
                >
                  予定管理の一覧でこの日へ移動
                </button>
              </div>
            </div>
            </>,
            document.body,
          )
        : null}
    </section>
  );
}
